import { useEffect, type ReactNode } from "react";
import { Button } from "@praesto/ui-kit";
import type { BootState } from "../boot";
import { applyTheme, getTheme, listenSystemTheme } from "../lib/theme";
import itLocale from "../locales/it.json";

// BootGate renders before the core i18n registry has the shell strings (the
// error can happen while modules load), so it reads its copy from it.json.
const copy = itLocale as Readonly<Record<string, string>>;

/** Renders the right shell for each boot phase (see boot.ts). */
export function BootGate({
  state,
  login,
  children,
  onReload = () => window.location.reload(),
}: {
  state: BootState;
  login: ReactNode;
  children: ReactNode;
  onReload?: () => void;
}) {
  // Apply theme at boot + react to system theme changes when in "system" mode.
  useEffect(() => {
    applyTheme(getTheme());
    return listenSystemTheme(() => {
      if (getTheme() === "system") applyTheme("system");
    });
  }, []);

  if (state.phase === "ready") return <>{children}</>;
  if (state.phase === "login") return <>{login}</>;

  return (
    <div className="min-h-screen bg-canvas text-fg-default flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <div className="text-eyebrow flex items-center justify-center gap-2">
          <span>Praesto</span>
        </div>
        {state.phase === "booting" && <p className="mt-6 text-caption">Avvio in corso…</p>}
        {state.phase === "no-tenant" && (
          <p className="mt-6 text-base leading-relaxed">
            Nessuna struttura indicata nell&apos;indirizzo. Usa il link della tua organizzazione
            (es. <code>/blackfin</code>).
          </p>
        )}
        {/* The technical message (state.message) is logged, never shown:
            it tells staff nothing. What they can do is reload (issue #401). */}
        {state.phase === "error" && (
          <div role="alert" className="mt-6 space-y-4">
            <p className="text-base leading-relaxed">{copy["boot.error.title"]}</p>
            <p className="text-sm leading-relaxed text-fg-mute">{copy["boot.error.body"]}</p>
            <Button variant="primary" onClick={onReload}>
              {copy["boot.error.reload"]}
            </Button>
          </div>
        )}
        {state.phase === "recognizing" && (
          <p className="mt-6 text-caption text-fg-muted">Ti riconosco…</p>
        )}
        {state.phase === "no-session" && (
          <div className="mt-6 space-y-4">
            <p className="text-base leading-relaxed">Accedi dalla tua organizzazione.</p>
            <a
              className="inline-block text-sm text-fg-mute hover:text-fg-default underline tap-target"
              href="https://www.praesto.mademind.it"
            >
              Vai al sito Praesto
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
